import { Link } from 'react-router-dom';
import { ArrowRight, Compass, ExternalLink, Heart, Leaf, MessageCircle, Route, ShieldCheck, Users } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Kicker, Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: Compass, keys: 'hiw.s1' },
  { icon: Route, keys: 'hiw.s2' },
  { icon: MessageCircle, keys: 'hiw.s3' },
  { icon: Heart, keys: 'hiw.s4' },
];

const PILLARS = [
  { icon: Users, keys: 'pil.1' },
  { icon: Heart, keys: 'pil.2' },
  { icon: Leaf, keys: 'pil.3' },
  { icon: ShieldCheck, keys: 'pil.4' },
];

export default function AboutPage() {
  const { t } = useLang();
  useSEO('Sobre | Eco Collection', 'A Eco Collection liga viajantes a profissionais locais de turismo em São Tomé e Príncipe e Cabo Verde.');

  return (
    <div data-testid="about-page">
      <PageHeader kicker={t('page.about.k')} title={t('page.about.t')} sub={t('page.about.s')} />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 text-center md:px-8">
          <Reveal>
            <Kicker>Eco Collection</Kicker>
            <p className="eco-body mt-6 text-eco-ink/75">{t('about.mission')}</p>
          </Reveal>
        </div>
      </section>

      <section id="our-story" data-testid="about-our-story" className="scroll-mt-24 bg-eco-deep py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal className="text-center">
            <Kicker>{t('story.about.k')}</Kicker>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-white md:text-4xl">{t('story.about.t')}</h2>
          </Reveal>
          <Reveal className="mt-10 space-y-5">
            <p className="eco-body text-white/75">{t('story.about.p1')}</p>
            <p className="eco-body text-white/75">{t('story.about.p2')}</p>
          </Reveal>
          <Reveal className="mt-8">
            <div data-testid="story-roots-block" className="rounded-2xl border border-white/10 bg-white/5 p-6 text-left">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-eco-gold">{t('story.about.roots.t')}</p>
              <p className="mt-3 text-sm leading-relaxed text-white/75">{t('story.about.roots.p')}</p>
              <a
                href="https://www.tripadvisor.com.br/Restaurant_Review-g480234-d15851572-Reviews-Mini_Restaurante_Bar_Cantinho_Barreto-Santo_Antonio_Principe.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="story-tripadvisor-link"
                className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-[10px] border border-eco-gold/40 px-4 text-xs font-semibold text-eco-gold transition-colors hover:bg-eco-gold/10"
              >
                {t('story.about.tripadvisor')} <ExternalLink size={13} />
              </a>
            </div>
          </Reveal>
          <Reveal className="mt-10" data-testid="story-gallery">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-eco-gold/80">{t('story.about.photos')}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {[
                { src: `${API}/files/eco-collection/uploads/7013f2b9-78fd-438a-b6ad-b18239705828.webp`, cap: 'story.about.c1' },
                { src: `${API}/files/eco-collection/uploads/b09df307-86fc-4158-97f7-b3d0af977c5b.webp`, cap: 'story.about.c2' },
                { src: `${API}/files/eco-collection/uploads/15294a34-31ac-4d54-b020-dc7ffb485acf.webp`, cap: 'story.about.c3' },
                { src: `${API}/files/eco-collection/uploads/5a32fd99-d10b-4972-b8d2-44ca734f8d88.webp`, cap: 'story.about.c4' },
              ].map((photo, i) => (
                <figure key={i} data-testid={`story-photo-${i}`} className="group overflow-hidden rounded-2xl border border-white/10">
                  <img src={photo.src} alt={t(photo.cap)} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:aspect-square" />
                  <figcaption className="bg-white/5 px-3 py-2 text-center text-[11px] font-medium text-white/60">{t(photo.cap)}</figcaption>
                </figure>
              ))}
            </div>
            <p className="mt-3 text-center text-[11px] text-white/65">{t('story.about.photosnote')}</p>
          </Reveal>
          <Reveal className="mt-12 grid gap-3 text-center sm:grid-cols-3" data-testid="story-evolution">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-eco-gold">Cantinho Barreto</p>
              <p className="mt-2 text-sm text-white/70">{t('story.about.e1')}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-eco-gold">{t('story.about.e2.t')}</p>
              <p className="mt-2 text-sm text-white/70">{t('story.about.e2')}</p>
            </div>
            <div className="rounded-2xl border border-eco-gold/30 bg-eco-gold/10 p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-eco-gold">Eco Collection</p>
              <p className="mt-2 text-sm text-white/70">{t('story.about.e3')}</p>
            </div>
          </Reveal>
          <Reveal>
            <p className="mt-8 text-center text-xs text-white/60">{t('story.about.note')}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-eco-cream py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal className="text-center">
            <Kicker>{t('hiw.kicker')}</Kicker>
            <h2 className="eco-section-title mx-auto mt-4 max-w-2xl text-eco-ink">{t('hiw.title')}</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.keys} delay={i * 0.1} className="text-center">
                <div className="relative mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-white">
                  <s.icon size={24} className="text-eco-gold" />
                  <span className="absolute -top-2 -right-2 text-xs font-semibold text-eco-gold">0{i + 1}</span>
                </div>
                <h3 className="eco-card-title mt-5">{t(`${s.keys}.t`)}</h3>
                <p className="eco-secondary mt-2 text-eco-ink/60">{t(`${s.keys}.d`)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="rounded-2xl bg-eco-deep px-7 py-12 md:px-12 md:py-16">
            <Reveal className="text-center">
              <Kicker>{t('resp.kicker')}</Kicker>
              <p className="mx-auto mt-4 max-w-2xl text-lg leading-[1.65] font-normal text-white/80">{t('resp.title')}</p>
            </Reveal>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {PILLARS.map((p, i) => (
                <Reveal key={p.keys} delay={i * 0.1} className="text-center">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/10">
                    <p.icon size={22} className="text-eco-gold" />
                  </div>
                  <h3 className="mt-4 text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-white">{t(`${p.keys}.t`)}</h3>
                  <p className="eco-secondary mt-2 text-white/55">{t(`${p.keys}.d`)}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-eco-cream py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-5 text-center md:px-8">
          <Reveal>
            <Kicker>{t('about.lf.k')}</Kicker>
            <p className="mt-6 leading-relaxed text-eco-ink/70">{t('about.lf.d')}</p>
            <Link
              to="/plan-my-trip"
              data-testid="about-plan-cta"
              className="group eco-btn-primary mt-8"
            >
              {t('nav.plan')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
